import { useCallback, useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';

interface ScheduledJob {
  id: string;
  name: string;
  trigger: string;
  next_run_time: string | null;
  status: 'paused' | 'active';
}

interface JobExecution {
  id: string;
  job_id: string;
  started_at: string;
  completed_at: string | null;
  /** Runtime value: the engine may report a status this build does not know. */
  status?: string | null;
  error_message?: string;
}

/**
 * Execution states this build can say something true about. Anything else —
 * an unknown value, an arbitrary string, a missing status — is `unknown`,
 * which is a claim of its own and is styled distinctly from a failure.
 */
type ExecutionStatus = 'success' | 'failure' | 'running' | 'unknown';

const KNOWN_EXECUTION_STATUSES: ReadonlySet<string> = new Set(['success', 'failure', 'running']);

const EXECUTION_STATUS_STYLE: Record<ExecutionStatus, string> = {
  success: 'text-[#39FF14] border-[#39FF14]/20 bg-[#39FF14]/5',
  running: 'text-blue-400 border-blue-400/20 bg-blue-400/5 animate-pulse',
  failure: 'text-[#FF0055] border-[#FF0055]/20 bg-[#FF0055]/5',
  unknown: 'text-gray-300 border-gray-500/30 bg-gray-500/5',
};

function normalizeExecutionStatus(raw: string | null | undefined): ExecutionStatus {
  if (typeof raw === 'string' && KNOWN_EXECUTION_STATUSES.has(raw)) {
    return raw as ExecutionStatus;
  }
  return 'unknown';
}

export function SchedulerTab() {
  const [jobs, setJobs] = useState<ScheduledJob[] | null>(null);
  const [history, setHistory] = useState<JobExecution[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dormant, setDormant] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDormant(false);
    let jobsFetched: ScheduledJob[] = [];
    let historyFetched: JobExecution[] = [];
    let schedulerDormant = false;
    let nextError: string | null = null;

    try {
      const jobsData = await engineFetchJson<{ jobs: ScheduledJob[] }>('/scheduler');
      jobsFetched = jobsData.jobs;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('503') || msg.includes('CES_SCHEDULER_ENABLED') || msg.includes('502') || msg.includes('504')) {
        schedulerDormant = true;
      } else {
        nextError = msg;
      }
    }

    try {
      const historyData = await engineFetchJson<{ history: JobExecution[] }>('/scheduler/history?limit=20');
      historyFetched = historyData.history;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('503') || msg.includes('CES_SCHEDULER_ENABLED') || msg.includes('502') || msg.includes('504')) {
        schedulerDormant = true;
      } else if (!nextError) {
        nextError = msg;
      }
    }

    setJobs(jobsFetched);
    setHistory(historyFetched);
    setDormant(schedulerDormant);
    setError(nextError);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAction = async (jobId: string, action: 'pause' | 'resume' | 'trigger') => {
    setActionLoading(`${jobId}-${action}`);
    try {
      await engineFetchJson(`/scheduler/${jobId}/${action}`, { method: 'POST' });
      // Reload states
      await load();
    } catch (err) {
      alert(`Action ${action} failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="p-6 font-mono text-sm text-gray-200 space-y-6 overflow-auto h-full max-h-[calc(100vh-140px)]">
      <div className="flex items-center justify-between border-b border-gray-800 pb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Scheduler Control</div>
          <div className="text-lg font-bold text-white mt-1">Routine Job Cron Execution</div>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="px-2 py-1 border border-gray-800 rounded hover:border-[#39FF14]/40 hover:text-[#39FF14] transition-colors disabled:opacity-50 text-xs"
        >
          {loading ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {loading && !jobs ? (
        <div className="text-xs text-gray-500 animate-pulse py-4">&gt; ESTABLISHING SCHEDULER PROTOCOL LINK...</div>
      ) : error ? (
        <div className="bg-[#FF0055]/5 border border-[#FF0055]/20 p-4 rounded text-xs space-y-2">
          <p className="text-[#FF0055] font-bold">&gt; SCHEDULER FAULT DETECTED</p>
          <p className="text-gray-400">{error}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {dormant && (
            <div className="bg-yellow-500/5 border border-yellow-500/20 p-4 rounded text-xs space-y-2">
              <p className="text-yellow-500 font-bold">&gt; DORMANT SCHEDULER PROTOCOL</p>
              <p className="text-gray-400">
                The active APScheduler service is currently dormant in this revision environment (CES_SCHEDULER_ENABLED is false). 
                Execution history below represents offline database logs. To activate background triggers, deploy the Cloud Run revision with the environment variable: 
                <code className="block mt-1.5 p-1 bg-black/40 rounded text-yellow-400 font-bold max-w-max">CES_SCHEDULER_ENABLED=true</code>
              </p>
            </div>
          )}
          {/* Active Cron Jobs Section */}
          <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Scheduled Routines</div>
            {!jobs || jobs.length === 0 ? (
              <p className="text-gray-500 text-xs py-2">No scheduled routines registered in engine.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500 uppercase tracking-wider text-left border-b border-gray-800">
                    <tr>
                      <th className="py-2 font-normal">Job ID / Name</th>
                      <th className="py-2 font-normal">Trigger / Cron</th>
                      <th className="py-2 font-normal">Next Execution</th>
                      <th className="py-2 font-normal">Status</th>
                      <th className="py-2 font-normal text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {jobs.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-800/10">
                        <td className="py-3 pr-2">
                          <span className="font-bold text-gray-200">{job.name}</span>
                          <span className="block text-[10px] text-gray-500">{job.id}</span>
                        </td>
                        <td className="py-3 text-gray-400 font-bold">{job.trigger}</td>
                        <td className="py-3 text-gray-400">
                          {job.next_run_time ? new Date(job.next_run_time).toLocaleString() : '—'}
                        </td>
                        <td className="py-3">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase border font-bold ${
                            job.status === 'active' 
                              ? 'text-[#39FF14] border-[#39FF14]/20 bg-[#39FF14]/5' 
                              : 'text-gray-500 border-gray-800 bg-gray-900/40'
                          }`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="py-3 text-right space-x-2">
                          <button
                            disabled={actionLoading !== null}
                            onClick={() => void handleAction(job.id, 'trigger')}
                            className="px-2 py-0.5 border border-gray-800 hover:border-blue-500/40 hover:text-blue-400 rounded transition-colors text-[10px] uppercase disabled:opacity-50"
                          >
                            Trigger
                          </button>
                          {job.status === 'active' ? (
                            <button
                              disabled={actionLoading !== null}
                              onClick={() => void handleAction(job.id, 'pause')}
                              className="px-2 py-0.5 border border-gray-800 hover:border-yellow-500/40 hover:text-yellow-400 rounded transition-colors text-[10px] uppercase disabled:opacity-50"
                            >
                              Pause
                            </button>
                          ) : (
                            <button
                              disabled={actionLoading !== null}
                              onClick={() => void handleAction(job.id, 'resume')}
                              className="px-2 py-0.5 border border-gray-800 hover:border-[#39FF14]/40 hover:text-[#39FF14] rounded transition-colors text-[10px] uppercase disabled:opacity-50"
                            >
                              Resume
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Job Execution History Log Section */}
          <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Execution History Logs</div>
            {!history || history.length === 0 ? (
              <p className="text-gray-500 text-xs py-2">No execution runs recorded.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500 uppercase tracking-wider text-left border-b border-gray-800">
                    <tr>
                      <th className="py-2 font-normal">Execution ID</th>
                      <th className="py-2 font-normal">Job ID</th>
                      <th className="py-2 font-normal">Started At</th>
                      <th className="py-2 font-normal">Completed At</th>
                      <th className="py-2 font-normal">Result State</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {history.map((run) => {
                      const status = normalizeExecutionStatus(run.status);
                      return (
                        <tr key={run.id} className="hover:bg-gray-800/10">
                          <td className="py-2 pr-2 font-semibold text-gray-400">
                            {run.id.slice(0, 8)}...
                          </td>
                          <td className="py-2 text-gray-400 font-semibold">{run.job_id}</td>
                          <td className="py-2 text-gray-500">
                            {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}
                          </td>
                          <td className="py-2 text-gray-500">
                            {run.completed_at ? new Date(run.completed_at).toLocaleString() : '—'}
                          </td>
                          <td className="py-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase border font-bold ${EXECUTION_STATUS_STYLE[status]}`}>
                              {status}
                            </span>
                            {run.error_message && (
                              <span className="block text-[10px] text-[#FF0055]/80 mt-1 max-w-lg truncate" title={run.error_message}>
                                Error: {run.error_message}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
